import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { SYSTEM_ALLOWED_DATA_SOURCES } from './dataSourcePolicy.js';
import { MODULE, ORGANIZATION_MODULE, THEME_SETTING } from '../types/module.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for Module entity.
 * Stored in 'org/{orgId}/modules' or global 'module' collection.
 *
 * Fields without groups are always persisted.
 * Fields with groups are persisted only when those groups are active.
 */
export const moduleDescriptor = {
    name: MODULE,
    label: 'Módulo',
    domain: 'modules',
    collectionPath: 'module',
    version: 1,
    description: 'Represents a module/action available in the system',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            // No groups = always persisted
            description: 'Unique module identifier',
        },
        {
            name: 'name',
            label: 'Nome',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.NAME,
            description: 'Module display name',
        },
        {
            name: 'description',
            label: 'Descrição',
            type: 'string',
            required: true,
            description: 'Module description',
        },
        {
            name: 'action',
            label: 'Ação',
            type: 'string',
            required: true,
            description: 'Action identifier for the module handler',
        },
        {
            name: 'actionProperties',
            label: 'Propriedades da ação',
            type: 'object',
            required: true,
            description: 'Configuration properties for the action',
            // Note: objectFields not defined because this is a flexible key-value object
        },
        {
            name: 'icon',
            label: 'Ícone',
            type: 'string',
            required: false,
            description: 'Icon identifier for UI display',
        },
        {
            name: 'allowMultipleInsertions',
            label: 'Permitir múltiplas inserções',
            type: 'boolean',
            required: false,
            defaultValue: false,
            description: 'Whether the module can be added multiple times',
        },
        {
            name: 'billingType',
            label: 'Tipo de cobrança',
            type: 'string',
            required: false,
            description: 'Type of billing for this module',
        },
        {
            name: 'value',
            label: 'Valor',
            type: 'number',
            required: false,
            description: 'Monetary value or cost of the module',
        },
        {
            name: 'paramsConfig',
            label: 'Configurações de parâmetros',
            type: 'object',
            required: false,
            description: 'Delta-only parameter values against the registered manifest defaults',
        },
        {
            name: 'tags',
            label: 'Tags',
            type: 'array',
            arrayItemType: 'string',
            required: false,
            // maxItems: 10 enforced in normalizeTags (FieldDescriptor has no maxItems — 0148 §8)
            description: 'Free classification tags (kebab-lowercase); max 10 via normalizeTags',
        },
        {
            name: 'platforms',
            label: 'Plataformas',
            type: 'array',
            arrayItemType: 'string',
            required: false,
            // Allowed values = MODULE_PLATFORMS; enforced in normalizePlatforms (0151)
            description: "Where the module has a programmed screen; absent = ['console'] via modulePlatforms()",
        },
    ],
};
/**
 * Descriptor for OrganizationModule entity.
 * Stored in 'org/{orgId}/modules'.
 * Links a module to an organization with optional overrides.
 */
export const organizationModuleDescriptor = {
    name: ORGANIZATION_MODULE,
    label: 'Módulo da organização',
    domain: 'modules',
    collectionPath: 'org/{orgId}/modules',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Links a module to an organization with optional customizations',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Unique organization-module link identifier',
        },
        {
            name: 'moduleId',
            label: 'ID do módulo',
            type: 'string',
            required: true,
            description: 'Reference to the base module',
        },
        {
            name: 'name',
            label: 'Nome personalizado',
            type: 'string',
            required: false,
            description: 'Optional custom name for this organization',
        },
        {
            name: 'description',
            label: 'Descrição personalizada',
            type: 'string',
            required: false,
            description: 'Optional custom description for this organization',
        },
        {
            name: 'icon',
            label: 'Ícone personalizado',
            type: 'string',
            required: false,
            description: 'Optional custom icon for this organization',
        },
        {
            name: 'action',
            label: 'Ação',
            type: 'string',
            required: false,
            description: 'Action type (redirect, group, etc.)',
        },
        {
            name: 'actionProperties',
            label: 'Propriedades da ação',
            type: 'object',
            required: false,
            description: 'Action-specific configuration',
        },
        {
            name: 'billingType',
            label: 'Tipo de cobrança personalizado',
            type: 'string',
            required: false,
            description: 'Optional billing type override for this organization',
        },
        {
            name: 'value',
            label: 'Valor personalizado',
            type: 'number',
            required: false,
            description: 'Optional value/cost override for this organization',
        },
        {
            name: 'paramsConfig',
            label: 'Configurações de parâmetros',
            type: 'object',
            required: false,
            description: 'Delta-only parameter values (module+org scope overrides only)',
        },
        {
            name: 'groupId',
            label: 'Grupo',
            type: 'string',
            required: false,
            description: 'ModuleGroup id this org-module appears under (undefined = root)',
        },
        {
            name: 'order',
            label: 'Ordem',
            type: 'number',
            required: false,
            description: 'Sort order within the parent group (or root)',
        },
    ],
    foreignKeys: [
        {
            fieldName: 'moduleId',
            targetEntity: 'Module',
            relationship: 'one-to-one',
            description: 'Reference to the base module definition',
        },
        {
            fieldName: 'groupId',
            targetEntity: 'ModuleGroup',
            relationship: 'one-to-one',
            description: 'Menu group this organization module belongs to',
        },
    ],
};
/**
 * Descriptor for ThemeSetting entity.
 * Stored in 'theme' collection (LocalStorage).
 * Used for storing user theme preferences.
 */
export const themeSettingDescriptor = {
    name: THEME_SETTING,
    label: 'Configuração de tema',
    domain: 'system',
    collectionPath: 'theme',
    version: 1,
    description: 'User theme preferences stored locally',
    repositoryConfig: {
        dataSource: DataSourceStrategy.LocalStorage,
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Setting identifier (typically "theme")',
        },
        {
            name: 'value',
            label: 'Modo do Tema',
            type: 'enum',
            required: true,
            enumValues: ['light', 'dark', 'system'],
            description: 'Selected theme mode',
        },
    ],
};
//# sourceMappingURL=module.descriptor.js.map